import React from 'react';
import { useTranslation } from '../contexts/LanguageContext';
import { splitLetters, upperWord, ordinal } from '../utils/letters';

const IS_TOUCH = typeof window !== 'undefined' && (window.matchMedia?.('(pointer: coarse)')?.matches || 'ontouchstart' in window);

/**
 * Guidance banner for onboarding, rendered ABOVE the board.
 *
 *  • Guided step (coached=true): reads the puzzle's optimal `solutionPath` +
 *    the player's progress, tells them which word to type next, and once it's
 *    typed correctly prompts Enter.
 *  • Solo step (coached=false): a gentle, no-spoiler nudge.
 *
 * Display-only — never touches input or submission.
 */
function describeChange(from, to, t, language) {
    // Compare whole letters so Croatian LJ/NJ/DŽ count as one position.
    from = splitLetters(upperWord(from || ''), language);
    to = splitLetters(upperWord(to || ''), language);
    if (from.length === to.length) {
        let pos = -1;
        for (let i = 0; i < from.length; i++) {
            if (from[i] !== to[i]) { pos = i; break; }
        }
        if (pos === -1) return '';
        return t('game.coaching.letterChanged', { word: from.join(''), ordinal: ordinal(pos + 1, language), letter: to[pos] });
    }
    if (to.length > from.length) return t('game.coaching.letterAdded', { word: from.join('') });
    return t('game.coaching.letterRemoved', { word: from.join('') });
}

export default function CoachingOverlay({ active, coached, solutionPath, coachingHints, previousGuesses = [], currentWord = '', won }) {
    const { t, language } = useTranslation();
    if (!active || won) return null;

    // Solo step — encourage, don't give the answer.
    if (!coached) {
        return (
            <div className="coaching-overlay" role="status" aria-live="polite">
                <span className="coaching-step">{t('game.coaching.yourTurn')}</span>
                <span className="coaching-instruction">{t('game.coaching.soloInstruction')}</span>
            </div>
        );
    }

    if (!Array.isArray(solutionPath) || solutionPath.length < 3) return null;
    // The player types the intermediate words only — typing the second-to-last
    // word wins (it's one letter from the target, which the game auto-bridges).
    // So moves = path length - 2, not - 1.
    const totalMoves = solutionPath.length - 2;
    const path = solutionPath.map(w => upperWord(String(w || '')));
    // Coach from the player's latest word, so a different valid first word doesn't desync the banner.
    const lastWord = previousGuesses.length ? upperWord(String(previousGuesses[previousGuesses.length - 1])) : path[0];
    const moveIndex = path.indexOf(lastWord);
    if (moveIndex === -1) {
        return (
            <div className="coaching-overlay" role="status" aria-live="polite">
                <span className="coaching-step">{t('game.coaching.goodWord')}</span>
                <span className="coaching-instruction">{t.rich('game.coaching.offPath', { b: <b /> }, { target: path[path.length - 1] })}</span>
            </div>
        );
    }
    if (totalMoves < 1 || moveIndex >= totalMoves) return null;

    const fromWord = upperWord(String(solutionPath[moveIndex] || ''));
    const expected = upperWord(String(solutionPath[moveIndex + 1] || ''));
    if (!expected) return null;

    const typedCorrect = upperWord(currentWord || '') === expected;

    return (
        <div className={`coaching-overlay${typedCorrect ? ' coaching-ready' : ''}`} role="status" aria-live="polite">
            <span className="coaching-step">{t('game.coaching.stepOf', { step: moveIndex + 1, total: totalMoves })}</span>
            {typedCorrect ? (
                <span className="coaching-instruction">
                    {t.rich(IS_TOUCH ? 'game.coaching.readyTouch' : 'game.coaching.readyKeyboard', { k: <kbd className="coaching-key" /> })}
                </span>
            ) : (
                <>
                    <span className="coaching-instruction">{t.rich('game.coaching.typeWord', { b: <b /> }, { word: expected })}</span>
                    <span className="coaching-hint">{coachingHints?.[moveIndex] || describeChange(fromWord, expected, t, language)}</span>
                </>
            )}
        </div>
    );
}
